import { css, html, LitElement } from "lit";
import { customElement } from "lit/decorators/custom-element.js";
import { sharedStyles } from "../styles/shared";
import { property } from "lit/decorators/property.js";
import "../atoms/progress-spinner";
import "../atoms/text";
import { Size } from "shared";
import { live } from "lit/directives/live.js";
import { when } from "lit/directives/when.js";

@customElement("bs-switch")
export class BsSwitch extends LitElement {
	static get styles() {
		return [
			sharedStyles,
			css`
				:host {
					display: inline-flex;
					user-select: none;
					position: relative;
					-webkit-user-select: none;
					--_switch-width: var(--switch-width, 60px);
					--_switch-height: var(--switch-height, 34px);
					--_switch-slider-height: calc(var(--_switch-height) * 0.75);
					--_switch-slider-left-right-padding: calc((var(--_switch-height) - var(--_switch-slider-height)) / 2);
				}

				:host(:not([loading])) {
					transition: 120ms ease-out transform;
				}

				:host([loading]) #label {
					opacity: 0;
					pointer-events: none;
				}

				#label {
					display: flex;
					gap: var(--space-s);
					align-items: center;
				}

				#switch {
					position: relative;
					display: inline-flex;
					width: var(--_switch-width);
					height: var(--_switch-height);
					transform: scale(1);
				}

				:host(:hover) #switch {
					transform: scale(1.05);
				}

				:host([disabled]) {
					opacity: 0.5;
					pointer-events: none;
				}

				:host([size="tiny"]) {
					--_switch-width: 35px;
					--_switch-height: 20px;
				}

				:host([size="small"]) {
					--_switch-width: 52px;
					--_switch-height: 30px;
				}

				:host([size="large"]) {
					--_switch-width: 88px;
					--_switch-height: 50px;
				}

				#input {
					opacity: 0;
					width: 0;
					height: 0;
				}

				#slider {
					position: absolute;
					cursor: pointer;
					top: 0;
					left: 0;
					right: 0;
					bottom: 0;
					transition: 120ms ease-out background;
					border-radius: 9999px;
					background: var(--switch-bg, var(--shade-normal));
					color: var(--switch-color, var(--background));
				}

				#slider:before {
					position: absolute;
					content: "";
					height: var(--_switch-slider-height);
					width: var(--_switch-slider-height);
					top: 50%;
					left: 0;
					background-color: currentColor;
					transition: 120ms ease-out transform;
					border-radius: 50%;
					transform: translate(var(--_switch-slider-left-right-padding), -50%);
				}

				#input:checked + #slider {
					background: var(--switch-bg-checked, var(--success-normal));
					color: var(--switch-color-checked, var(--background));
				}

				#input:checked + #slider:before {
					transform: translate(
						calc(var(--_switch-width) - var(--_switch-slider-height) - var(--_switch-slider-left-right-padding)),
						-50%
					);
				}

				#spinner {
					--progress-spinner-size: 16px;
					position: absolute;
					top: 50%;
					transform: translateY(-50%);
					right: 0;
				}

				.text {
					line-height: 1;
				}
			`
		];
	}

	@property({ type: String, reflect: true }) size: Size = "medium";
	@property({ type: Boolean, reflect: true }) checked = false;
	@property({ type: Boolean, reflect: true }) disabled = false;
	@property({ type: Boolean, reflect: true }) loading = false;
	@property({ type: String, attribute: "after-text" }) afterText: string | undefined;
	@property({ type: String, attribute: "before-text" }) beforeText: string | undefined;

	onCheckboxInput(e: Event) {
		this.checked = (e.target as HTMLInputElement).checked ?? false;
		this.dispatchEvent(new CustomEvent("update", { detail: this.checked }));
	}

	render() {
		return html`
			${when(this.loading, () => html`<bs-progress-spinner id="spinner"></bs-progress-spinner>`)}
			<label id="label">
				<slot name="before"></slot>
				${when(
					this.beforeText,
					() => html`<bs-text class="text" ?label="${this.checked}">${this.beforeText}</bs-text>`
				)}
				<div id="switch">
					<input
						id="input"
						type="checkbox"
						?checked="${live(this.checked)}"
						?disabled="${this.disabled}"
						@input="${this.onCheckboxInput}"
					/>
					<span id="slider"></span>
				</div>
				${when(this.afterText, () => html`<bs-text class="text" ?label="${!this.checked}">${this.afterText}</bs-text>`)}
				<slot></slot>
			</label>
		`;
	}
}
